import { useState } from "react";
import {
  Bell,
  CheckCircle2,
  Clock,
  CreditCard,
  QrCode,
  ScanLine,
  UtensilsCrossed,
} from "lucide-react";
import { HOW_IT_WORKS_GUEST_STEPS, HOW_IT_WORKS_VENUE_STEPS } from "../content/landing";
import { useT } from "../i18n";
import { cardClass, sectionHeading, sectionSubheading } from "../lib/ui";

const STEP_ICONS = {
  scan: QrCode,
  menu: UtensilsCrossed,
  pay: CreditCard,
  track: Clock,
  alert: Bell,
  prepare: UtensilsCrossed,
  ready: CheckCircle2,
  handover: ScanLine,
} as const;

type Audience = "guests" | "venue";

export default function HowItWorks() {
  const translate = useT();
  const [audience, setAudience] = useState<Audience>("guests");
  const steps = audience === "guests" ? HOW_IT_WORKS_GUEST_STEPS : HOW_IT_WORKS_VENUE_STEPS;
  const subtitleKey = audience === "guests" ? "howItWorksSubtitle" : "howItWorksVenueSubtitle";

  return (
    <section
      id="how-it-works"
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24"
      aria-labelledby="how-it-works-heading"
    >
      <div className="max-w-2xl">
        <h2
          id="how-it-works-heading"
          className={`${sectionHeading} flex flex-wrap items-center gap-x-3 gap-y-3`}
        >
          <span>{translate("howItWorksTitle")}</span>
          <span
            className="inline-flex rounded-xl border border-[var(--border-subtle)] bg-surface p-1 align-middle text-base font-semibold sm:text-lg"
            role="group"
            aria-label={`${translate("howItWorksToggleGuests")} / ${translate("howItWorksToggleVenue")}`}
          >
            <AudienceButton
              active={audience === "guests"}
              onClick={() => setAudience("guests")}
              label={translate("howItWorksToggleGuests")}
            />
            <AudienceButton
              active={audience === "venue"}
              onClick={() => setAudience("venue")}
              label={translate("howItWorksToggleVenue")}
            />
          </span>
        </h2>
        <p className={sectionSubheading}>{translate(subtitleKey)}</p>
      </div>

      <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, index) => {
          const Icon = STEP_ICONS[step.icon];
          return (
            <li key={`${audience}-${step.icon}`} className={`${cardClass} relative p-6`}>
              <span className="absolute right-4 top-4 font-display text-4xl font-bold text-accent/20">
                {index + 1}
              </span>
              <div className="mb-4 inline-flex rounded-xl bg-brand-soft p-3">
                <Icon className="h-6 w-6 text-accent" aria-hidden />
              </div>
              <h3 className="font-display text-lg font-semibold">{translate(step.titleKey)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {translate(step.bodyKey)}
              </p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function AudienceButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition ${
        active
          ? "bg-brand-gradient text-white shadow-glow"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
